import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { Save, Upload, Trash2, Image, Globe, MessageSquare, FileText, Settings2, RefreshCw, Calculator, Plus, X, Palette, Type, Key, ExternalLink, Shield, AlertTriangle, Lock, Languages, Scale, ChevronDown, Menu, Heart, Play, Mail, Bell, HelpCircle, Star, Wrench, ChevronUp, Cog, GripVertical, LayoutGrid, Share2, Users, Newspaper, FileSignature, Calendar, UserCog, KeyRound } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { settingsApi, mediaApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { THEMES, FONTS } from '@/context/ThemeContext';
import { useAdminLanguage } from '@/context/AdminLanguageContext';
import AdminAccounts from './AdminAccounts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tab configuration - will be translated dynamically
const getTabsConfig = (t) => [
  { value: 'general', label: t('settings_tab_general'), icon: Settings2 },
  { value: 'modules', label: t('settings_tab_modules'), icon: Settings2 },
  { value: 'design', label: t('settings_tab_design'), icon: Palette },
  { value: 'seo', label: t('settings_tab_seo'), icon: Globe },
  { value: 'legal', label: t('settings_tab_legal'), icon: Scale },
  { value: 'api-keys', label: t('settings_tab_api'), icon: Key },
  { value: 'accounts', label: t('settings_tab_accounts'), icon: UserCog },
];

// Module definitions for sortable list - will be translated dynamically
const getHomepageModules = (t) => [
  { id: 'featured', key: 'module_featured_cars_enabled', label: t('module_featured'), icon: Star, hasSettings: true },
  { id: 'brands', key: 'module_brands_enabled', label: t('module_brands'), icon: Star, hasSettings: true },
  { id: 'about', key: 'module_about_enabled', label: t('module_about'), icon: FileText, hasSettings: true },
  { id: 'video', key: 'module_video_enabled', label: t('module_video'), icon: Play, hasSettings: true },
  { id: 'services', key: 'module_services_enabled', label: t('module_services'), icon: Wrench, hasSettings: true },
  { id: 'team', key: 'module_team_enabled', label: t('module_team'), icon: Users, hasSettings: true, description: t('module_team_desc'), homepageKey: 'module_team_homepage_enabled' },
  { id: 'blog', key: 'module_blog_enabled', label: t('module_blog'), icon: Newspaper, hasSettings: true, description: t('module_blog_desc'), homepageKey: 'module_blog_homepage_enabled' },
  { id: 'newsletter', key: 'module_newsletter_enabled', label: t('module_newsletter'), icon: Mail, hasSettings: true },
  { id: 'contact', key: 'module_contact_enabled', label: t('module_contact'), icon: MessageSquare, hasSettings: true },
  { id: 'faq', key: 'module_faq_enabled', label: t('module_faq'), icon: HelpCircle, hasSettings: true },
];

// Default module order
const DEFAULT_MODULE_ORDER = ['featured', 'brands', 'about', 'video', 'services', 'team', 'blog', 'newsletter', 'contact', 'faq'];

// Feature modules (not sortable, just toggles) - will be translated dynamically
const getFeatureModules = (t) => [
  { id: 'favorites', key: 'module_favorites_enabled', label: t('module_favorites'), description: t('module_favorites_desc'), icon: Heart },
  { id: 'compare', key: 'module_compare_enabled', label: t('module_compare'), description: t('module_compare_desc'), icon: Scale },
  { id: 'financing', key: 'module_financing_enabled', label: t('module_financing'), description: t('module_financing_desc'), icon: Calculator, hasSettings: true },
  { id: 'chat', key: 'module_chat_enabled', label: t('module_chat'), description: t('module_chat_desc'), icon: MessageSquare },
  { id: 'price_alerts', key: 'module_price_alerts_enabled', label: t('module_price_alerts'), description: t('module_price_alerts_desc'), icon: Bell },
  { id: 'test-drive', key: 'module_test_drive_enabled', label: t('module_test_drive'), description: t('module_test_drive_desc'), icon: Calendar, hasSettings: true },
];

export default function AdminSettings() {
  const navigate = useNavigate();
  const { t } = useAdminLanguage();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoSaved, setAutoSaved] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [editLang, setEditLang] = useState('de'); // 'de' or 'en'
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const fileInputRef = useRef(null);
  const [uploadTarget, setUploadTarget] = useState(null);
  const saveTimeoutRef = useRef(null);

  // Get translated config
  const TABS = getTabsConfig(t);
  const HOMEPAGE_MODULES = getHomepageModules(t);
  const FEATURE_MODULES = getFeatureModules(t);

  // Check URL for tab parameter (e.g., ?tab=modules)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get('tab');
    if (tabParam) {
      setActiveTab(tabParam);
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    loadSettings();
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, []);

  const loadSettings = async () => {
    try {
      const response = await settingsApi.get();
      setSettings(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  // Auto-save with debounce
  const autoSave = async (newSettings) => {
    setSaving(true);
    try {
      await settingsApi.update(newSettings);
      setAutoSaved(true);
      setTimeout(() => setAutoSaved(false), 2000);
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    const newSettings = { ...settings, [field]: value };
    setSettings(newSettings);
    
    // Clear previous timeout and set new one for auto-save
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      autoSave(newSettings);
    }, 1000);
  };

  const handleSave = async () => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    setSaving(true);
    try {
      await settingsApi.update(settings);
      toast.success('Einstellungen gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const response = await mediaApi.upload(file);
      const url = response.data.url;
      handleChange(uploadTarget, url);
      toast.success('Bild hochgeladen');
    } catch (error) {
      toast.error('Fehler beim Hochladen');
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const openFileUpload = (target) => {
    setUploadTarget(target);
    fileInputRef.current?.click();
  };

  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${API_URL}${url}`;
  };

  // Get the current module order (from settings or default)
  const getModulesOrder = () => {
    return settings?.modules_order || DEFAULT_MODULE_ORDER;
  };

  // Move module up in order
  const moveModuleUp = (moduleId) => {
    const order = [...getModulesOrder()];
    const index = order.indexOf(moduleId);
    if (index > 0) {
      [order[index - 1], order[index]] = [order[index], order[index - 1]];
      handleChange('modules_order', order);
    }
  };

  // Move module down in order
  const moveModuleDown = (moduleId) => {
    const order = [...getModulesOrder()];
    const index = order.indexOf(moduleId);
    if (index < order.length - 1) {
      [order[index], order[index + 1]] = [order[index + 1], order[index]];
      handleChange('modules_order', order);
    }
  };

  // Get sorted modules based on current order
  const getSortedModules = () => {
    const order = getModulesOrder();
    return [...HOMEPAGE_MODULES].sort((a, b) => {
      const aIndex = order.indexOf(a.id);
      const bIndex = order.indexOf(b.id);
      // Put modules not in order at the end
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });
  };

  // Helper to get the correct field name based on language
  const getField = (baseField) => {
    if (editLang === 'en') {
      return `${baseField}_en`;
    }
    return baseField;
  };

  // Helper to get the value for current language
  const getValue = (baseField) => {
    const field = getField(baseField);
    return settings?.[field] || '';
  };

  // Helper to set the value for current language
  const setLangValue = (baseField, value) => {
    const field = getField(baseField);
    handleChange(field, value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="admin-settings" className="max-w-4xl">
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileUpload}
        accept="image/*"
        className="hidden"
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-light">{t('settings_title')}</h1>
          <p className="text-sm text-[#666666] mt-1">{t('settings_title') === 'Settings' ? 'Customize your website' : 'Passen Sie Ihre Website individuell an'}</p>
        </div>
        <div className="flex gap-2 items-center">
          {/* Language Toggle for Editing */}
          <div className="flex items-center bg-[#F9F9F7] rounded-lg p-1 mr-2">
            <button
              onClick={() => setEditLang('de')}
              className={`px-3 py-1.5 text-sm font-medium rounded transition-colors flex items-center gap-1.5 ${
                editLang === 'de' 
                  ? 'bg-white text-[#0A0A0A] shadow-sm' 
                  : 'text-[#666666] hover:text-[#0A0A0A]'
              }`}
              data-testid="edit-lang-de"
            >
              🇩🇪 DE
            </button>
            <button
              onClick={() => setEditLang('en')}
              className={`px-3 py-1.5 text-sm font-medium rounded transition-colors flex items-center gap-1.5 ${
                editLang === 'en' 
                  ? 'bg-white text-[#0A0A0A] shadow-sm' 
                  : 'text-[#666666] hover:text-[#0A0A0A]'
              }`}
              data-testid="edit-lang-en"
            >
              🇬🇧 EN
            </button>
          </div>
          
          {/* Auto-save status indicator */}
          <div className="flex items-center gap-2" data-testid="save-status">
            {saving && (
              <span className="text-sm text-[#666666] flex items-center gap-2">
                <RefreshCw size={16} className="animate-spin" />
                {t('msg_loading')}
              </span>
            )}
            {autoSaved && !saving && (
              <span className="text-sm text-green-600 flex items-center gap-2">
                <Save size={16} />
                ✓ {t('msg_save_success')}
              </span>
            )}
            {!saving && !autoSaved && (
              <span className="text-xs text-[#999999]">
                {t('settings_title') === 'Settings' ? 'Auto-save active' : 'Automatisches Speichern aktiv'}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Language Info Banner */}
      {editLang === 'en' && (
        <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-3">
          <Languages size={20} className="text-blue-600" />
          <p className="text-sm text-blue-800">
            {t('settings_title') === 'Settings' 
              ? <><strong>Editing English texts:</strong> Changes will be displayed for English-speaking visitors.</>
              : <><strong>Englische Texte bearbeiten:</strong> Änderungen werden für englischsprachige Besucher angezeigt.</>
            }
          </p>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        {/* Mobile Dropdown */}
        <div className="md:hidden mb-6 relative">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="w-full flex items-center justify-between p-4 bg-white border border-[#E5E5E5] rounded-lg"
          >
            <div className="flex items-center gap-2">
              {(() => {
                const currentTab = TABS.find(tab => tab.value === activeTab);
                const Icon = currentTab?.icon || Settings2;
                return (
                  <>
                    <Icon size={18} className="text-[#666666]" />
                    <span className="font-medium">{currentTab?.label || t('settings_tab_general')}</span>
                  </>
                );
              })()}
            </div>
            <ChevronDown size={20} className={`text-[#666666] transition-transform ${mobileMenuOpen ? 'rotate-180' : ''}`} />
          </button>
          
          {mobileMenuOpen && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#E5E5E5] rounded-lg shadow-lg z-50 overflow-hidden">
              {TABS.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.value}
                    onClick={() => {
                      setActiveTab(tab.value);
                      setMobileMenuOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 p-4 text-left hover:bg-[#F9F9F7] transition-colors ${
                      activeTab === tab.value ? 'bg-[#F9F9F7] font-medium' : ''
                    }`}
                  >
                    <Icon size={18} className={activeTab === tab.value ? 'text-gold' : 'text-[#666666]'} />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Desktop Tabs */}
        <TabsList className="hidden md:flex mb-6 bg-[#F9F9F7] p-1 rounded-lg flex-wrap gap-1">
          <TabsTrigger value="general" className="flex items-center gap-2 data-[state=active]:bg-white">
            <Settings2 size={16} />
            Allgemein
          </TabsTrigger>
          <TabsTrigger value="modules" className="flex items-center gap-2 data-[state=active]:bg-white">
            <Settings2 size={16} />
            Module
          </TabsTrigger>
          <TabsTrigger value="design" className="flex items-center gap-2 data-[state=active]:bg-white">
            <Palette size={16} />
            Design
          </TabsTrigger>
          <TabsTrigger value="seo" className="flex items-center gap-2 data-[state=active]:bg-white">
            <Globe size={16} />
            SEO
          </TabsTrigger>
          <TabsTrigger value="legal" className="flex items-center gap-2 data-[state=active]:bg-white">
            <Scale size={16} />
            Rechtliches
          </TabsTrigger>
          <TabsTrigger value="api-keys" className="flex items-center gap-2 data-[state=active]:bg-white">
            <Key size={16} />
            API Keys
          </TabsTrigger>
          <TabsTrigger value="accounts" className="flex items-center gap-2 data-[state=active]:bg-white">
            <UserCog size={16} />
            Accounts
          </TabsTrigger>
        </TabsList>

        {/* General Settings */}
        <TabsContent value="general" className="space-y-6">
          <div className="bg-white p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-4">Branding</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="overline block mb-2">Website-Name</label>
                <input
                  type="text"
                  value={settings.site_name || ''}
                  onChange={(e) => handleChange('site_name', e.target.value)}
                  className="input-premium w-full"
                  data-testid="site-name-input"
                />
              </div>
              <div>
                <label className="overline block mb-2">
                  Slogan/Tagline {editLang === 'en' && <span className="text-blue-600">(EN)</span>}
                </label>
                <input
                  type="text"
                  value={getValue('site_tagline')}
                  onChange={(e) => setLangValue('site_tagline', e.target.value)}
                  className="input-premium w-full"
                  placeholder={editLang === 'en' ? 'Premium Vehicles' : 'Premium Fahrzeuge'}
                />
              </div>
            </div>

            {/* Logo & Favicon */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              <div>
                <label className="overline block mb-2">Logo</label>
                <div className="flex items-center gap-4">
                  {settings.logo_url && (
                    <img src={getImageUrl(settings.logo_url)} alt="Logo" className="h-12 object-contain" />
                  )}
                  <Button
                    type="button"
                    onClick={() => openFileUpload('logo_url')}
                    className="btn-secondary flex items-center gap-2"
                  >
                    <Upload size={16} />
                    Hochladen
                  </Button>
                  {settings.logo_url && (
                    <Button
                      type="button"
                      onClick={() => handleChange('logo_url', null)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 size={16} />
                    </Button>
                  )}
                </div>
              </div>
              <div>
                <label className="overline block mb-2">Favicon</label>
                <div className="flex items-center gap-4">
                  {settings.favicon_url && (
                    <img src={getImageUrl(settings.favicon_url)} alt="Favicon" className="h-8 w-8 object-contain" />
                  )}
                  <Button
                    type="button"
                    onClick={() => openFileUpload('favicon_url')}
                    className="btn-secondary flex items-center gap-2"
                  >
                    <Upload size={16} />
                    Hochladen
                  </Button>
                  {settings.favicon_url && (
                    <Button
                      type="button"
                      onClick={() => handleChange('favicon_url', null)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 size={16} />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Website URL (for emails) */}
          <div className="bg-white p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-2">Website-URL</h3>
            <p className="text-sm text-[#666666] mb-4">
              Diese URL wird für Links in E-Mails verwendet (z.B. "Antworten"-Button, Newsletter-Links).
            </p>
            <div>
              <label className="overline block mb-2">Website-URL</label>
              <input
                type="url"
                value={settings.site_url || ''}
                onChange={(e) => handleChange('site_url', e.target.value)}
                className="input-premium w-full"
                placeholder="https://ihre-domain.at"
                data-testid="site-url-input"
              />
              <p className="text-xs text-amber-600 mt-2">
                ⚠️ Wichtig: Geben Sie Ihre Produktions-Domain ein (z.B. https://tim-automobile.at)
              </p>
            </div>
          </div>

          {/* Quick Contact Info */}
          <div className="bg-white p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-2">Kontakt-Schnellzugriff</h3>
            <p className="text-sm text-[#666666] mb-4">
              Diese Nummer wird auf der Fahrzeug-Detailseite, im Footer und in der Kontaktbox angezeigt. Klickbar als Telefonlink.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="overline block mb-2">Telefon</label>
                <input
                  type="tel"
                  value={settings.contact_phone || ''}
                  onChange={(e) => handleChange('contact_phone', e.target.value)}
                  className="input-premium w-full"
                  placeholder="+43 1 234 5678"
                  data-testid="general-contact-phone-input"
                />
              </div>
              <div>
                <label className="overline block mb-2">E-Mail</label>
                <input
                  type="email"
                  value={settings.contact_email || ''}
                  onChange={(e) => handleChange('contact_email', e.target.value)}
                  className="input-premium w-full"
                  placeholder="kontakt@ihre-domain.at"
                  data-testid="general-contact-email-input"
                />
              </div>
              <div>
                <label className="overline block mb-2">Adresse</label>
                <input
                  type="text"
                  value={settings.contact_address || ''}
                  onChange={(e) => handleChange('contact_address', e.target.value)}
                  className="input-premium w-full"
                  placeholder="Musterstraße 123, 1010 Wien"
                  data-testid="general-contact-address-input"
                />
              </div>
              <div>
                <label className="overline block mb-2">Öffnungszeiten</label>
                <input
                  type="text"
                  value={settings.contact_hours || ''}
                  onChange={(e) => handleChange('contact_hours', e.target.value)}
                  className="input-premium w-full"
                  placeholder="Mo-Fr: 9-18 Uhr"
                  data-testid="general-contact-hours-input"
                />
              </div>
            </div>
          </div>

          {/* NYZA-STUDIO Branding */}
          <div className="bg-white p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-2">NYZA-STUDIO Branding</h3>
            <p className="text-sm text-[#666666] mb-4">
              Verwalten Sie Logos für Light und Dark Mode. Wenn kein Logo hochgeladen ist, wird ein eleganter Schriftzug verwendet.
            </p>
            <button
              onClick={() => navigate('/admin/settings/module/nyza-studio')}
              className="flex items-center gap-3 w-full p-4 rounded-lg border-2 border-gold bg-gold/5 hover:bg-gold/10 transition-colors"
              data-testid="nyza-studio-settings-link"
            >
              <div className="w-10 h-10 rounded-lg bg-gold text-white flex items-center justify-center">
                <Image size={20} />
              </div>
              <div className="flex-1 text-left">
                <h4 className="font-medium">Logo-Verwaltung</h4>
                <p className="text-xs text-[#666666]">Separate Logos für Light/Dark Mode hochladen</p>
              </div>
              <Cog size={20} className="text-[#999999]" />
            </button>
          </div>

          {/* Hero Section */}
          <div className="bg-white p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-4">
              Hero-Bereich (Startseite) {editLang === 'en' && <span className="text-blue-600 text-sm font-normal">(Englisch)</span>}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="overline block mb-2">
                  Überschrift {editLang === 'en' && <span className="text-blue-600">(EN)</span>}
                </label>
                <input
                  type="text"
                  value={getValue('header_title')}
                  onChange={(e) => setLangValue('header_title', e.target.value)}
                  className="input-premium w-full"
                  placeholder={editLang === 'en' ? 'Find Your Dream Car' : 'Finden Sie Ihr Traumauto'}
                  data-testid="header-title-input"
                />
              </div>
              <div>
                <label className="overline block mb-2">
                  Untertitel {editLang === 'en' && <span className="text-blue-600">(EN)</span>}
                </label>
                <textarea
                  value={getValue('header_subtitle')}
                  onChange={(e) => setLangValue('header_subtitle', e.target.value)}
                  className="input-premium w-full h-24 resize-none"
                  placeholder={editLang === 'en' ? 'Discover our curated collection...' : 'Entdecken Sie unsere kuratierte Sammlung...'}
                />
              </div>
              <div>
                <label className="overline block mb-2">Hero-Hintergrund</label>
                <div className="space-y-4">
                  {/* Image Option */}
                  <div className="flex items-center gap-4">
                    {settings.hero_image_url && !settings.hero_video_url && (
                      <img src={getImageUrl(settings.hero_image_url)} alt="Hero" className="h-20 object-cover rounded" />
                    )}
                    <Button
                      type="button"
                      onClick={() => openFileUpload('hero_image_url')}
                      className="btn-secondary flex items-center gap-2"
                    >
                      <Upload size={16} />
                      Bild hochladen
                    </Button>
                  </div>
                  
                  {/* Video Option */}
                  <div className="pt-4 border-t border-[#E5E5E5]">
                    <label className="overline block mb-2">Oder: YouTube-Video (alternativ zum Bild)</label>
                    <p className="text-xs text-[#666666] mb-2">
                      Video wird automatisch geloopt, ohne Ton und ohne YouTube-Buttons/Watermark angezeigt.
                    </p>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={settings.hero_video_url || ''}
                        onChange={(e) => handleChange('hero_video_url', e.target.value)}
                        className="input-premium flex-1"
                        placeholder="YouTube Video-URL (z.B. https://www.youtube.com/watch?v=...)"
                      />
                      {settings.hero_video_url && (
                        <Button
                          type="button"
                          onClick={() => handleChange('hero_video_url', '')}
                          className="btn-secondary text-red-500"
                        >
                          <X size={16} />
                        </Button>
                      )}
                    </div>
                    {settings.hero_video_url && (
                      <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-sm text-green-700">
                        ✓ Video wird als Hintergrund verwendet (Bild wird überschrieben)
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation / Menu Labels */}
          <div className="bg-white p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-4">
              Menü-Bezeichnungen {editLang === 'en' && <span className="text-blue-600 text-sm font-normal">(Englisch)</span>}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="overline block mb-2">Home {editLang === 'en' && <span className="text-blue-600">(EN)</span>}</label>
                <input
                  type="text"
                  value={getValue('nav_home')}
                  onChange={(e) => setLangValue('nav_home', e.target.value)}
                  className="input-premium w-full"
                  placeholder="Home"
                />
              </div>
              <div>
                <label className="overline block mb-2">Fahrzeuge {editLang === 'en' && <span className="text-blue-600">(EN)</span>}</label>
                <input
                  type="text"
                  value={getValue('nav_inventory')}
                  onChange={(e) => setLangValue('nav_inventory', e.target.value)}
                  className="input-premium w-full"
                  placeholder={editLang === 'en' ? 'Vehicles' : 'Fahrzeuge'}
                />
              </div>
              <div>
                <label className="overline block mb-2">Über uns {editLang === 'en' && <span className="text-blue-600">(EN)</span>}</label>
                <input
                  type="text"
                  value={getValue('nav_about')}
                  onChange={(e) => setLangValue('nav_about', e.target.value)}
                  className="input-premium w-full"
                  placeholder={editLang === 'en' ? 'About Us' : 'Über uns'}
                />
              </div>
              <div>
                <label className="overline block mb-2">Kontakt {editLang === 'en' && <span className="text-blue-600">(EN)</span>}</label>
                <input
                  type="text"
                  value={getValue('nav_contact')}
                  onChange={(e) => setLangValue('nav_contact', e.target.value)}
                  className="input-premium w-full"
                  placeholder={editLang === 'en' ? 'Contact' : 'Kontakt'}
                />
              </div>
              <div>
                <label className="overline block mb-2">Merkliste {editLang === 'en' && <span className="text-blue-600">(EN)</span>}</label>
                <input
                  type="text"
                  value={getValue('nav_favorites')}
                  onChange={(e) => setLangValue('nav_favorites', e.target.value)}
                  className="input-premium w-full"
                  placeholder={editLang === 'en' ? 'Favorites' : 'Merkliste'}
                />
              </div>
            </div>
          </div>

          {/* Social Share / OpenGraph */}
          <div className="bg-white p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-4">Share-Text (Social Media)</h3>
            <p className="text-sm text-[#666666] mb-4">
              Diese Texte werden angezeigt, wenn die Website auf WhatsApp, Facebook, Twitter etc. geteilt wird.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="overline block mb-2">Titel {editLang === 'en' && <span className="text-blue-600">(EN)</span>}</label>
                <input
                  type="text"
                  value={getValue('share_title')}
                  onChange={(e) => setLangValue('share_title', e.target.value)}
                  className="input-premium w-full"
                  placeholder={editLang === 'en' ? 'Tim Automobile - Premium Vehicles' : 'Tim Automobile - Premium Fahrzeuge'}
                  data-testid="share-title-input"
                />
              </div>
              <div>
                <label className="overline block mb-2">Beschreibung {editLang === 'en' && <span className="text-blue-600">(EN)</span>}</label>
                <input
                  type="text"
                  value={getValue('share_description')}
                  onChange={(e) => setLangValue('share_description', e.target.value)}
                  className="input-premium w-full"
                  placeholder={editLang === 'en' ? 'Your partner for exclusive premium vehicles.' : 'Ihr Partner für exklusive Premium-Fahrzeuge.'}
                  data-testid="share-description-input"
                />
              </div>
            </div>
            <p className="text-xs text-[#999999] mt-3">
              💡 Tipp: Das Logo wird automatisch als Vorschaubild verwendet.
            </p>
          </div>

          {/* Copyright */}
          <div className="bg-white p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-4">Footer</h3>
            <p className="text-sm text-[#666666] mb-4">
              Der Copyright-Text wird im Footer unten angezeigt.
            </p>
            <div>
              <label className="overline block mb-2">Copyright-Text</label>
              <input
                type="text"
                value={settings.footer_copyright || 'Tim Automobile'}
                onChange={(e) => handleChange('footer_copyright', e.target.value)}
                className="input-premium w-full"
                placeholder="© 2025 Firmenname. Alle Rechte vorbehalten."
                data-testid="copyright-input"
              />
              <p className="text-xs text-[#999999] mt-1">
                Beispiel: "© 2025 Tim Automobile" oder "Tim Automobile GmbH"
              </p>
            </div>
            
            {/* Opening Hours */}
            <div className="mt-6 pt-6 border-t border-[#E5E5E5]">
              <h4 className="font-heading text-base mb-4">Öffnungszeiten</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="overline block mb-2">Wochentage {editLang === 'en' && <span className="text-blue-600">(EN)</span>}</label>
                  <input
                    type="text"
                    value={getValue('footer_hours_weekday')}
                    onChange={(e) => setLangValue('footer_hours_weekday', e.target.value)}
                    className="input-premium w-full"
                    placeholder={editLang === 'en' ? 'Monday - Friday: 9:00 - 18:00' : 'Montag - Freitag: 9:00 - 18:00'}
                  />
                </div>
                <div>
                  <label className="overline block mb-2">Samstag {editLang === 'en' && <span className="text-blue-600">(EN)</span>}</label>
                  <input
                    type="text"
                    value={getValue('footer_hours_saturday')}
                    onChange={(e) => setLangValue('footer_hours_saturday', e.target.value)}
                    className="input-premium w-full"
                    placeholder={editLang === 'en' ? 'Saturday: 10:00 - 14:00' : 'Samstag: 10:00 - 14:00'}
                  />
                </div>
                <div>
                  <label className="overline block mb-2">Sonntag {editLang === 'en' && <span className="text-blue-600">(EN)</span>}</label>
                  <input
                    type="text"
                    value={getValue('footer_hours_sunday')}
                    onChange={(e) => setLangValue('footer_hours_sunday', e.target.value)}
                    className="input-premium w-full"
                    placeholder={editLang === 'en' ? 'Sunday: By appointment' : 'Sonntag: Nach Vereinbarung'}
                  />
                </div>
              </div>
            </div>
            
            {/* Footer Description */}
            <div className="mt-6 pt-6 border-t border-[#E5E5E5]">
              <label className="overline block mb-2">
                Footer-Beschreibung {editLang === 'en' && <span className="text-blue-600">(EN)</span>}
              </label>
              <textarea
                value={getValue('footer_description')}
                onChange={(e) => setLangValue('footer_description', e.target.value)}
                className="input-premium w-full h-24 resize-none"
                placeholder={editLang === 'en' ? 'Your premier destination for exotic and luxury vehicles.' : 'Ihr Premier-Ziel für exotische und luxuriöse Fahrzeuge.'}
              />
            </div>
          </div>
        </TabsContent>

        {/* Modules */}
        <TabsContent value="modules" className="space-y-6">
          {/* Homepage Module Ordering */}
          <div className="bg-white p-6 border border-[#E5E5E5] rounded-lg">
            <div className="flex items-center gap-3 mb-2">
              <LayoutGrid size={20} className="text-gold" />
              <h3 className="font-heading text-lg">Startseiten-Module sortieren</h3>
            </div>
            <p className="text-sm text-[#666666] mb-6">
              Verwenden Sie die Pfeil-Buttons, um die Reihenfolge der Module auf der Startseite zu ändern. 
              Klicken Sie auf das Zahnrad-Symbol, um die Einstellungen eines Moduls zu bearbeiten.
            </p>
            
            <div className="space-y-2">
              {getSortedModules().map((module, index) => {
                const Icon = module.icon;
                const isEnabled = settings[module.key] !== false;
                const order = getModulesOrder();
                const isFirst = order.indexOf(module.id) === 0;
                const isLast = order.indexOf(module.id) === order.length - 1;
                
                // For Team and Blog: main toggle enables menu, secondary toggle for homepage
                const hasHomepageToggle = module.homepageKey;
                const isHomepageEnabled = hasHomepageToggle ? settings[module.homepageKey] !== false : false;
                
                return (
                  <div 
                    key={module.id}
                    className={`flex flex-col gap-2 p-4 rounded-lg border-2 transition-all ${
                      isEnabled ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-gray-50 opacity-60'
                    }`}
                    data-testid={`module-item-${module.id}`}
                  >
                    <div className="flex items-center gap-3">
                      {/* Position Number */}
                      <span className="w-6 h-6 rounded-full bg-[#0A0A0A] text-white text-xs flex items-center justify-center font-medium">
                        {index + 1}
                      </span>
                      
                      {/* Module Icon & Info */}
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        isEnabled ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-500'
                      }`}>
                        <Icon size={20} />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium">{module.label}</h4>
                        {hasHomepageToggle && isEnabled && (
                          <p className="text-xs text-[#666666]">
                            ✓ Im Menü {isHomepageEnabled ? '• ✓ Auf Startseite' : ''}
                          </p>
                        )}
                        {module.description && !hasHomepageToggle && (
                          <p className="text-xs text-[#666666]">{module.description}</p>
                        )}
                      </div>
                      
                      {/* Sort Arrows */}
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => moveModuleUp(module.id)}
                          disabled={isFirst}
                          className={`p-1.5 rounded hover:bg-gray-200 transition-colors ${
                            isFirst ? 'opacity-30 cursor-not-allowed' : ''
                          }`}
                          title="Nach oben"
                          data-testid={`move-up-${module.id}`}
                        >
                          <ChevronUp size={18} />
                        </button>
                        <button
                          onClick={() => moveModuleDown(module.id)}
                          disabled={isLast}
                          className={`p-1.5 rounded hover:bg-gray-200 transition-colors ${
                            isLast ? 'opacity-30 cursor-not-allowed' : ''
                          }`}
                          title="Nach unten"
                          data-testid={`move-down-${module.id}`}
                        >
                          <ChevronDown size={18} />
                        </button>
                      </div>
                      
                      {/* Settings Gear (for modules with dedicated settings) */}
                      {module.hasSettings && (
                        <button
                          onClick={() => navigate(`/admin/settings/module/${module.id}`)}
                          className="p-2 rounded-lg hover:bg-gray-200 transition-colors text-[#666666] hover:text-[#0A0A0A]"
                          title="Einstellungen bearbeiten"
                          data-testid={`settings-${module.id}`}
                        >
                          <Cog size={18} />
                        </button>
                      )}
                      
                      {/* Toggle Switch - Main module toggle */}
                      <label className="relative inline-flex items-center cursor-pointer" title={hasHomepageToggle ? "Modul aktivieren (erscheint im Menü)" : "Aktivieren"}>
                        <input
                          type="checkbox"
                          checked={isEnabled}
                          onChange={(e) => handleChange(module.key, e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                      </label>
                    </div>
                    
                    {/* Additional toggle for Team/Blog: Show on Homepage */}
                    {hasHomepageToggle && isEnabled && (
                      <div className="flex items-center justify-between pl-12 pt-2 border-t border-gray-200 mt-2">
                        <span className="text-sm text-[#666666]">Auch auf Startseite anzeigen</span>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isHomepageEnabled}
                            onChange={(e) => handleChange(module.homepageKey, e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                        </label>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Feature Toggles (not sortable) */}
          <div className="bg-white p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-2">Funktionen aktivieren/deaktivieren</h3>
            <p className="text-sm text-[#666666] mb-6">
              Diese Funktionen sind seitenübergreifend und erscheinen nicht als Module auf der Startseite.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {FEATURE_MODULES.map((module) => {
                const Icon = module.icon;
                const isEnabled = settings[module.key] !== false;
                
                return (
                  <div 
                    key={module.id}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      isEnabled ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          isEnabled ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-500'
                        }`}>
                          <Icon size={20} />
                        </div>
                        <div>
                          <h4 className="font-medium">{module.label}</h4>
                          <p className="text-xs text-[#666666]">{module.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {module.hasSettings && (
                          <button
                            onClick={() => navigate(`/admin/settings/module/${module.id}`)}
                            className="p-2 rounded-lg hover:bg-white/50 transition-colors"
                            title="Einstellungen"
                          >
                            <Cog size={18} className="text-[#666666]" />
                          </button>
                        )}
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isEnabled}
                            onChange={(e) => handleChange(module.key, e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                        </label>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>Hinweis:</strong> Deaktivierte Module werden auf der Website vollständig ausgeblendet. 
                Die zugehörigen Menüpunkte und Funktionen verschwinden automatisch.
              </p>
            </div>

            {/* Social Media Section */}
            <div className="mt-8 pt-6 border-t border-[#E5E5E5]">
              <h3 className="font-heading text-lg mb-4">Social Media Footer</h3>
              <p className="text-sm text-[#666666] mb-4">
                Verknüpfen Sie Ihre Social-Media-Profile. Die Icons erscheinen automatisch im Footer, wenn URLs hinterlegt sind.
              </p>
              <button
                onClick={() => navigate('/admin/settings/module/social')}
                className="flex items-center gap-3 w-full p-4 rounded-lg border-2 border-gold bg-gold/5 hover:bg-gold/10 transition-colors"
              >
                <div className="w-10 h-10 rounded-lg bg-gold text-white flex items-center justify-center">
                  <Share2 size={20} />
                </div>
                <div className="flex-1 text-left">
                  <h4 className="font-medium">Social Media Links</h4>
                  <p className="text-xs text-[#666666]">Facebook, Instagram, TikTok, YouTube, LinkedIn, X</p>
                </div>
                <Cog size={20} className="text-[#999999]" />
              </button>
            </div>
          </div>
        </TabsContent>

        {/* SEO */}
        <TabsContent value="seo" className="space-y-6">
          <div className="bg-white p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-4">SEO-Einstellungen</h3>
            <div>
              <label className="overline block mb-2">Meta-Titel</label>
              <input
                type="text"
                value={settings.seo_title || ''}
                onChange={(e) => handleChange('seo_title', e.target.value)}
                className="input-premium w-full"
                maxLength={60}
                data-testid="seo-title-input"
              />
              <p className="text-xs text-[#999999] mt-1">{(settings.seo_title || '').length}/60 Zeichen</p>
            </div>
            <div className="mt-4">
              <label className="overline block mb-2">Meta-Beschreibung</label>
              <textarea
                value={settings.seo_description || ''}
                onChange={(e) => handleChange('seo_description', e.target.value)}
                className="input-premium w-full h-24 resize-none"
                maxLength={160}
              />
              <p className="text-xs text-[#999999] mt-1">{(settings.seo_description || '').length}/160 Zeichen</p>
            </div>
            <div className="mt-4">
              <label className="overline block mb-2">Keywords (kommagetrennt)</label>
              <input
                type="text"
                value={settings.seo_keywords || ''}
                onChange={(e) => handleChange('seo_keywords', e.target.value)}
                className="input-premium w-full"
                placeholder="Luxusautos, Premium Fahrzeuge, Sportwagen"
              />
            </div>
            
            {/* Preview */}
            <div className="mt-6 p-4 bg-[#F9F9F7] rounded-lg">
              <p className="overline mb-2">Google-Vorschau</p>
              <div className="bg-white p-3 rounded border">
                <p className="text-blue-600 text-lg hover:underline cursor-pointer">
                  {settings.seo_title || 'Seitentitel'}
                </p>
                <p className="text-green-700 text-sm">
                  premierwheels.preview.emergentagent.com
                </p>
                <p className="text-sm text-[#666666]">
                  {settings.seo_description || 'Meta-Beschreibung wird hier angezeigt...'}
                </p>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Legal Pages */}
        <TabsContent value="legal" className="space-y-6">
          <div className="bg-white p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-2">Rechtliche Seiten</h3>
            <p className="text-sm text-[#666666] mb-6">
              Bearbeiten Sie den Inhalt Ihrer rechtlichen Seiten. HTML-Tags werden unterstützt.
            </p>
            
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-6">
              <p className="text-sm text-blue-800">
                <strong>Tipp:</strong> Sie können HTML-Tags wie <code className="bg-blue-100 px-1 rounded">&lt;h2&gt;</code>, <code className="bg-blue-100 px-1 rounded">&lt;p&gt;</code>, <code className="bg-blue-100 px-1 rounded">&lt;ul&gt;</code>, <code className="bg-blue-100 px-1 rounded">&lt;strong&gt;</code> verwenden um Ihren Text zu formatieren.
              </p>
            </div>
          </div>

          {/* Datenschutz */}
          <div className="bg-white p-6 border border-[#E5E5E5] rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg">Datenschutzerklärung</h3>
              <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="text-sm text-gold hover:underline flex items-center gap-1">
                <ExternalLink size={14} /> Vorschau
              </a>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="overline block mb-2">Seitentitel (Deutsch)</label>
                  <input
                    type="text"
                    value={settings.page_privacy_title || 'Datenschutzerklärung'}
                    onChange={(e) => handleChange('page_privacy_title', e.target.value)}
                    className="input-premium w-full"
                  />
                </div>
                <div>
                  <label className="overline block mb-2">Seitentitel (Englisch)</label>
                  <input
                    type="text"
                    value={settings.page_privacy_title_en || 'Privacy Policy'}
                    onChange={(e) => handleChange('page_privacy_title_en', e.target.value)}
                    className="input-premium w-full"
                  />
                </div>
              </div>
              <div>
                <label className="overline block mb-2">🇩🇪 Inhalt (Deutsch)</label>
                <textarea
                  value={settings.page_privacy_content || ''}
                  onChange={(e) => handleChange('page_privacy_content', e.target.value)}
                  className="input-premium w-full h-48 resize-y font-mono text-sm"
                  placeholder="Ihre Datenschutzerklärung hier eingeben..."
                />
              </div>
              <div>
                <label className="overline block mb-2">🇬🇧 Inhalt (Englisch)</label>
                <textarea
                  value={settings.page_privacy_content_en || ''}
                  onChange={(e) => handleChange('page_privacy_content_en', e.target.value)}
                  className="input-premium w-full h-48 resize-y font-mono text-sm"
                  placeholder="Your privacy policy here..."
                />
              </div>
            </div>
          </div>

          {/* AGB */}
          <div className="bg-white p-6 border border-[#E5E5E5] rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg">Allgemeine Geschäftsbedingungen</h3>
              <a href="/agb" target="_blank" rel="noopener noreferrer" className="text-sm text-gold hover:underline flex items-center gap-1">
                <ExternalLink size={14} /> Vorschau
              </a>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="overline block mb-2">Seitentitel (Deutsch)</label>
                  <input
                    type="text"
                    value={settings.page_terms_title || 'Allgemeine Geschäftsbedingungen'}
                    onChange={(e) => handleChange('page_terms_title', e.target.value)}
                    className="input-premium w-full"
                  />
                </div>
                <div>
                  <label className="overline block mb-2">Seitentitel (Englisch)</label>
                  <input
                    type="text"
                    value={settings.page_terms_title_en || 'Terms and Conditions'}
                    onChange={(e) => handleChange('page_terms_title_en', e.target.value)}
                    className="input-premium w-full"
                  />
                </div>
              </div>
              <div>
                <label className="overline block mb-2">🇩🇪 Inhalt (Deutsch)</label>
                <textarea
                  value={settings.page_terms_content || ''}
                  onChange={(e) => handleChange('page_terms_content', e.target.value)}
                  className="input-premium w-full h-48 resize-y font-mono text-sm"
                  placeholder="Ihre AGB hier eingeben..."
                />
              </div>
              <div>
                <label className="overline block mb-2">🇬🇧 Inhalt (Englisch)</label>
                <textarea
                  value={settings.page_terms_content_en || ''}
                  onChange={(e) => handleChange('page_terms_content_en', e.target.value)}
                  className="input-premium w-full h-48 resize-y font-mono text-sm"
                  placeholder="Your terms and conditions here..."
                />
              </div>
            </div>
          </div>

          {/* Impressum */}
          <div className="bg-white p-6 border border-[#E5E5E5] rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg">Impressum</h3>
              <a href="/impressum" target="_blank" rel="noopener noreferrer" className="text-sm text-gold hover:underline flex items-center gap-1">
                <ExternalLink size={14} /> Vorschau
              </a>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="overline block mb-2">Seitentitel (Deutsch)</label>
                  <input
                    type="text"
                    value={settings.page_imprint_title || 'Impressum'}
                    onChange={(e) => handleChange('page_imprint_title', e.target.value)}
                    className="input-premium w-full"
                  />
                </div>
                <div>
                  <label className="overline block mb-2">Seitentitel (Englisch)</label>
                  <input
                    type="text"
                    value={settings.page_imprint_title_en || 'Imprint'}
                    onChange={(e) => handleChange('page_imprint_title_en', e.target.value)}
                    className="input-premium w-full"
                  />
                </div>
              </div>
              <div>
                <label className="overline block mb-2">🇩🇪 Inhalt (Deutsch)</label>
                <textarea
                  value={settings.page_imprint_content || ''}
                  onChange={(e) => handleChange('page_imprint_content', e.target.value)}
                  className="input-premium w-full h-48 resize-y font-mono text-sm"
                  placeholder="Ihr Impressum hier eingeben..."
                />
              </div>
              <div>
                <label className="overline block mb-2">🇬🇧 Inhalt (Englisch)</label>
                <textarea
                  value={settings.page_imprint_content_en || ''}
                  onChange={(e) => handleChange('page_imprint_content_en', e.target.value)}
                  className="input-premium w-full h-48 resize-y font-mono text-sm"
                  placeholder="Your imprint here..."
                />
              </div>
            </div>
          </div>

          {/* Vorbehalten */}
          <div className="bg-white p-6 border border-[#E5E5E5] rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg">Vorbehalten</h3>
              <a href="/vorbehalten" target="_blank" rel="noopener noreferrer" className="text-sm text-gold hover:underline flex items-center gap-1">
                <ExternalLink size={14} /> Vorschau
              </a>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="overline block mb-2">Seitentitel (Deutsch)</label>
                  <input
                    type="text"
                    value={settings.page_reserved_title || 'Vorbehalten'}
                    onChange={(e) => handleChange('page_reserved_title', e.target.value)}
                    className="input-premium w-full"
                  />
                </div>
                <div>
                  <label className="overline block mb-2">Seitentitel (Englisch)</label>
                  <input
                    type="text"
                    value={settings.page_reserved_title_en || 'Reserved'}
                    onChange={(e) => handleChange('page_reserved_title_en', e.target.value)}
                    className="input-premium w-full"
                  />
                </div>
              </div>
              <div>
                <label className="overline block mb-2">🇩🇪 Inhalt (Deutsch)</label>
                <textarea
                  value={settings.page_reserved_content || ''}
                  onChange={(e) => handleChange('page_reserved_content', e.target.value)}
                  className="input-premium w-full h-48 resize-y font-mono text-sm"
                  placeholder="Inhalt für Vorbehalten-Seite..."
                />
              </div>
              <div>
                <label className="overline block mb-2">🇬🇧 Inhalt (Englisch)</label>
                <textarea
                  value={settings.page_reserved_content_en || ''}
                  onChange={(e) => handleChange('page_reserved_content_en', e.target.value)}
                  className="input-premium w-full h-48 resize-y font-mono text-sm"
                  placeholder="Content for reserved page..."
                />
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Design */}
        <TabsContent value="design" className="space-y-6">
          {/* Hero Design Settings - moved from General */}
          <div className="bg-white p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-4">Hero-Design & Buttons</h3>
            <p className="text-sm text-[#666666] mb-6">
              Passen Sie die Farben und Buttons im Hero-Bereich an.
            </p>
            
            {/* Text Colors */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="overline block mb-2">Überschrift-Farbe</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={settings.hero_text_color || '#FFFFFF'}
                    onChange={(e) => handleChange('hero_text_color', e.target.value)}
                    className="w-10 h-10 rounded cursor-pointer border border-[#E5E5E5]"
                  />
                  <input
                    type="text"
                    value={settings.hero_text_color || '#FFFFFF'}
                    onChange={(e) => handleChange('hero_text_color', e.target.value)}
                    className="input-premium flex-1 font-mono text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="overline block mb-2">Untertitel-Farbe</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={settings.hero_subtitle_color || '#CCCCCC'}
                    onChange={(e) => handleChange('hero_subtitle_color', e.target.value)}
                    className="w-10 h-10 rounded cursor-pointer border border-[#E5E5E5]"
                  />
                  <input
                    type="text"
                    value={settings.hero_subtitle_color || '#CCCCCC'}
                    onChange={(e) => handleChange('hero_subtitle_color', e.target.value)}
                    className="input-premium flex-1 font-mono text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Primary Button */}
            <div className="border border-[#E5E5E5] rounded-lg p-4 mb-4">
              <h4 className="font-medium mb-4">Primärer Button</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="overline block mb-2">
                    Button-Text {editLang === 'en' && <span className="text-blue-600">(EN)</span>}
                  </label>
                  <input
                    type="text"
                    value={editLang === 'en' ? (settings.hero_button_text_en || 'Vehicles') : (settings.hero_button_text || 'Fahrzeuge')}
                    onChange={(e) => handleChange(editLang === 'en' ? 'hero_button_text_en' : 'hero_button_text', e.target.value)}
                    className="input-premium w-full"
                  />
                </div>
                <div>
                  <label className="overline block mb-2">Link/Ziel</label>
                  <select
                    value={settings.hero_button_link || '/cars'}
                    onChange={(e) => handleChange('hero_button_link', e.target.value)}
                    className="input-premium w-full"
                  >
                    <optgroup label="Seiten">
                      <option value="/cars">Fahrzeuge (/cars)</option>
                      <option value="/favorites">Merkliste (/favorites)</option>
                      <option value="/compare">Vergleichen (/compare)</option>
                    </optgroup>
                    <optgroup label="Anker (gleiche Seite)">
                      <option value="#featured">Empfohlene Fahrzeuge (#featured)</option>
                      <option value="#about">Über uns (#about)</option>
                      <option value="#contact">Kontakt (#contact)</option>
                      <option value="#video">Video (#video)</option>
                    </optgroup>
                  </select>
                </div>
                <div>
                  <label className="overline block mb-2">Hintergrund</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={settings.hero_button_bg_color || '#FFFFFF'}
                      onChange={(e) => handleChange('hero_button_bg_color', e.target.value)}
                      className="w-10 h-10 rounded cursor-pointer border border-[#E5E5E5]"
                    />
                    <input
                      type="text"
                      value={settings.hero_button_bg_color || '#FFFFFF'}
                      onChange={(e) => handleChange('hero_button_bg_color', e.target.value)}
                      className="input-premium flex-1 font-mono text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="overline block mb-2">Textfarbe</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={settings.hero_button_text_color || '#000000'}
                      onChange={(e) => handleChange('hero_button_text_color', e.target.value)}
                      className="w-10 h-10 rounded cursor-pointer border border-[#E5E5E5]"
                    />
                    <input
                      type="text"
                      value={settings.hero_button_text_color || '#000000'}
                      onChange={(e) => handleChange('hero_button_text_color', e.target.value)}
                      className="input-premium flex-1 font-mono text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Secondary Button */}
            <div className="border border-[#E5E5E5] rounded-lg p-4">
              <h4 className="font-medium mb-4">Sekundärer Button (Outline)</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="overline block mb-2">
                    Button-Text {editLang === 'en' && <span className="text-blue-600">(EN)</span>}
                  </label>
                  <input
                    type="text"
                    value={editLang === 'en' ? (settings.hero_button_secondary_text_en || 'Learn More') : (settings.hero_button_secondary_text || 'Mehr erfahren')}
                    onChange={(e) => handleChange(editLang === 'en' ? 'hero_button_secondary_text_en' : 'hero_button_secondary_text', e.target.value)}
                    className="input-premium w-full"
                  />
                </div>
                <div>
                  <label className="overline block mb-2">Link/Ziel</label>
                  <select
                    value={settings.hero_button_secondary_link || '#about'}
                    onChange={(e) => handleChange('hero_button_secondary_link', e.target.value)}
                    className="input-premium w-full"
                  >
                    <optgroup label="Seiten">
                      <option value="/cars">Fahrzeuge (/cars)</option>
                      <option value="/favorites">Merkliste (/favorites)</option>
                      <option value="/compare">Vergleichen (/compare)</option>
                    </optgroup>
                    <optgroup label="Anker (gleiche Seite)">
                      <option value="#featured">Empfohlene Fahrzeuge (#featured)</option>
                      <option value="#about">Über uns (#about)</option>
                      <option value="#contact">Kontakt (#contact)</option>
                      <option value="#video">Video (#video)</option>
                    </optgroup>
                  </select>
                </div>
                <div>
                  <label className="overline block mb-2">Rahmenfarbe</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={settings.hero_button_secondary_border_color || '#FFFFFF'}
                      onChange={(e) => handleChange('hero_button_secondary_border_color', e.target.value)}
                      className="w-10 h-10 rounded cursor-pointer border border-[#E5E5E5]"
                    />
                    <input
                      type="text"
                      value={settings.hero_button_secondary_border_color || '#FFFFFF'}
                      onChange={(e) => handleChange('hero_button_secondary_border_color', e.target.value)}
                      className="input-premium flex-1 font-mono text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="overline block mb-2">Textfarbe</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={settings.hero_button_secondary_text_color || '#FFFFFF'}
                      onChange={(e) => handleChange('hero_button_secondary_text_color', e.target.value)}
                      className="w-10 h-10 rounded cursor-pointer border border-[#E5E5E5]"
                    />
                    <input
                      type="text"
                      value={settings.hero_button_secondary_text_color || '#FFFFFF'}
                      onChange={(e) => handleChange('hero_button_secondary_text_color', e.target.value)}
                      className="input-premium flex-1 font-mono text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Preview */}
            <div className="mt-6 p-6 rounded-lg relative overflow-hidden" style={{ backgroundColor: '#1a1a1a' }}>
              <p className="text-xs text-[#666666] mb-4 uppercase tracking-wider">Vorschau</p>
              <h2 
                className="text-2xl font-heading mb-2"
                style={{ color: settings.hero_text_color || '#FFFFFF' }}
              >
                {getValue('header_title') || 'Finden Sie Ihr Traumauto'}
              </h2>
              <p 
                className="text-sm mb-4"
                style={{ color: settings.hero_subtitle_color || '#CCCCCC' }}
              >
                {getValue('header_subtitle')?.substring(0, 60) || 'Entdecken Sie unsere kuratierte Sammlung...'}...
              </p>
              <div className="flex gap-3">
                <button
                  className="px-4 py-2 rounded font-medium text-sm"
                  style={{ 
                    backgroundColor: settings.hero_button_bg_color || '#FFFFFF',
                    color: settings.hero_button_text_color || '#000000'
                  }}
                >
                  {editLang === 'en' ? (settings.hero_button_text_en || 'Vehicles') : (settings.hero_button_text || 'Fahrzeuge')}
                </button>
                <button
                  className="px-4 py-2 rounded font-medium text-sm border-2 bg-transparent"
                  style={{ 
                    borderColor: settings.hero_button_secondary_border_color || '#FFFFFF',
                    color: settings.hero_button_secondary_text_color || '#FFFFFF'
                  }}
                >
                  {editLang === 'en' ? (settings.hero_button_secondary_text_en || 'Learn More') : (settings.hero_button_secondary_text || 'Mehr erfahren')}
                </button>
              </div>
            </div>
          </div>

          {/* Font Selection */}
          <div className="bg-white p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-4 flex items-center gap-2">
              <Type size={20} />
              Schriftart
            </h3>
            <p className="text-sm text-[#666666] mb-6">Wählen Sie die Schriftart für Ihre Website</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(FONTS).map(([key, font]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleChange('design_font', key)}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${
                    (settings.design_font || 'classic') === key 
                      ? 'border-gold bg-gold/5' 
                      : 'border-[#E5E5E5] hover:border-[#999999]'
                  }`}
                  data-testid={`font-${key}`}
                >
                  <div className="mb-2">
                    <span 
                      className="text-xl font-semibold block"
                      style={{ fontFamily: font.heading }}
                    >
                      {font.name}
                    </span>
                  </div>
                  <p 
                    className="text-sm text-[#666666]"
                    style={{ fontFamily: font.body }}
                  >
                    {font.description}
                  </p>
                  <div className="mt-3 pt-3 border-t border-[#E5E5E5]">
                    <p 
                      className="text-xs text-[#999999]"
                      style={{ fontFamily: font.body }}
                    >
                      Aa Bb Cc 1234 - Beispieltext
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Custom Colors */}
          <div className="bg-white p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-4">Farbeinstellungen</h3>
            <p className="text-sm text-[#666666] mb-6">Passen Sie die Akzentfarbe an Ihre Marke an</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="overline block mb-2">Primärfarbe / Akzent</label>
                <div className="flex items-center gap-4">
                  <input
                    type="color"
                    value={settings.design_primary_color || '#D4AF37'}
                    onChange={(e) => handleChange('design_primary_color', e.target.value)}
                    className="w-12 h-12 rounded cursor-pointer border border-[#E5E5E5]"
                    data-testid="primary-color-input"
                  />
                  <input
                    type="text"
                    value={settings.design_primary_color || '#D4AF37'}
                    onChange={(e) => handleChange('design_primary_color', e.target.value)}
                    className="input-premium flex-1 font-mono"
                    placeholder="#D4AF37"
                  />
                </div>
                <div className="flex gap-2 mt-3">
                  {['#D4AF37', '#00FF88', '#FF6B6B', '#4ECDC4', '#9B59B6', '#E74C3C'].map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => handleChange('design_primary_color', color)}
                      className={`w-8 h-8 rounded-full border-2 ${
                        settings.design_primary_color === color ? 'border-[#0A0A0A]' : 'border-transparent'
                      }`}
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                  ))}
                </div>
              </div>
              
              <div>
                <label className="overline block mb-2">Sekundärfarbe</label>
                <div className="flex items-center gap-4">
                  <input
                    type="color"
                    value={settings.design_secondary_color || '#0A0A0A'}
                    onChange={(e) => handleChange('design_secondary_color', e.target.value)}
                    className="w-12 h-12 rounded cursor-pointer border border-[#E5E5E5]"
                  />
                  <input
                    type="text"
                    value={settings.design_secondary_color || '#0A0A0A'}
                    onChange={(e) => handleChange('design_secondary_color', e.target.value)}
                    className="input-premium flex-1 font-mono"
                    placeholder="#0A0A0A"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Dark Mode Settings */}
          <div className="bg-white p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-4">Dunkelmodus</h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Dunkelmodus erlauben</p>
                  <p className="text-sm text-[#666666]">Benutzer können zwischen Hell und Dunkel wechseln</p>
                </div>
                <button
                  type="button"
                  onClick={() => handleChange('design_dark_mode_enabled', !settings.design_dark_mode_enabled)}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    settings.design_dark_mode_enabled !== false ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                  data-testid="dark-mode-enabled-toggle"
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    settings.design_dark_mode_enabled !== false ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Standard-Modus</p>
                  <p className="text-sm text-[#666666]">Wird beim ersten Besuch verwendet</p>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleChange('design_default_mode', 'light')}
                    className={`px-4 py-2 text-sm rounded ${
                      (settings.design_default_mode || 'light') === 'light' 
                        ? 'bg-[#0A0A0A] text-white' 
                        : 'bg-[#F9F9F7] text-[#666666]'
                    }`}
                  >
                    ☀️ Hell
                  </button>
                  <button
                    type="button"
                    onClick={() => handleChange('design_default_mode', 'dark')}
                    className={`px-4 py-2 text-sm rounded ${
                      settings.design_default_mode === 'dark' 
                        ? 'bg-[#0A0A0A] text-white' 
                        : 'bg-[#F9F9F7] text-[#666666]'
                    }`}
                  >
                    🌙 Dunkel
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Header Icon Settings */}
          <div className="bg-white p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-4">Header-Einstellungen</h3>
            <p className="text-sm text-[#666666] mb-6">
              Passen Sie das Verhalten und Aussehen des Headers an.
            </p>
            
            {/* Sticky & Style Options */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              <div>
                <label className="overline block mb-2">Header-Verhalten</label>
                <div className="flex items-center gap-3 mt-2">
                  <input
                    type="checkbox"
                    id="header-sticky"
                    checked={settings.design_header_sticky !== false}
                    onChange={(e) => handleChange('design_header_sticky', e.target.checked)}
                    className="w-5 h-5"
                  />
                  <label htmlFor="header-sticky" className="text-sm">
                    Sticky (fixiert beim Scrollen)
                  </label>
                </div>
              </div>
              
              <div>
                <label className="overline block mb-2">Header-Stil</label>
                <select
                  value={settings.design_header_style || 'transparent'}
                  onChange={(e) => handleChange('design_header_style', e.target.value)}
                  className="input-premium w-full"
                >
                  <option value="transparent">Transparent (milchig)</option>
                  <option value="solid">Solide Farbe</option>
                </select>
              </div>
              
              <div>
                <label className="overline block mb-2">Hintergrundfarbe</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={settings.design_header_bg_color || '#0A0A0A'}
                    onChange={(e) => handleChange('design_header_bg_color', e.target.value)}
                    className="w-10 h-10 rounded cursor-pointer border border-[#E5E5E5]"
                    disabled={settings.design_header_style !== 'solid'}
                  />
                  <input
                    type="text"
                    value={settings.design_header_bg_color || '#0A0A0A'}
                    onChange={(e) => handleChange('design_header_bg_color', e.target.value)}
                    className="input-premium flex-1 font-mono text-sm"
                    disabled={settings.design_header_style !== 'solid'}
                  />
                </div>
                <p className="text-xs text-[#999999] mt-1">
                  Nur bei "Solide Farbe" aktiv
                </p>
              </div>
              
              <div>
                <label className="overline block mb-2">Blur-Effekt</label>
                <div className="flex items-center gap-3 mt-2">
                  <input
                    type="checkbox"
                    id="header-blur"
                    checked={settings.design_header_blur !== false}
                    onChange={(e) => handleChange('design_header_blur', e.target.checked)}
                    className="w-5 h-5"
                    disabled={settings.design_header_style === 'solid'}
                  />
                  <label htmlFor="header-blur" className="text-sm">
                    Blur aktiviert
                  </label>
                </div>
                <p className="text-xs text-[#999999] mt-1">
                  Nur bei "Transparent" aktiv
                </p>
              </div>
            </div>
            
            {/* Icon Color */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="overline block mb-2">Header Icon/Text Farbe</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={settings.design_header_icon_color || '#FFFFFF'}
                    onChange={(e) => handleChange('design_header_icon_color', e.target.value)}
                    className="w-10 h-10 rounded cursor-pointer border border-[#E5E5E5]"
                    data-testid="header-icon-color"
                  />
                  <input
                    type="text"
                    value={settings.design_header_icon_color || '#FFFFFF'}
                    onChange={(e) => handleChange('design_header_icon_color', e.target.value)}
                    className="input-premium flex-1 font-mono text-sm"
                    placeholder="#FFFFFF"
                  />
                </div>
                <p className="text-xs text-[#999999] mt-2">
                  Tipp: Weiß (#FFFFFF) für dunkle Header, Schwarz (#000000) für helle Header
                </p>
              </div>
              
              <div>
                <label className="overline block mb-2">Vorschau</label>
                <div 
                  className={`p-4 rounded-lg flex items-center gap-4 ${settings.design_header_blur !== false && settings.design_header_style !== 'solid' ? 'backdrop-blur-md' : ''}`}
                  style={{ 
                    backgroundColor: settings.design_header_style === 'solid' 
                      ? (settings.design_header_bg_color || '#0A0A0A')
                      : `${settings.design_header_bg_color || '#0A0A0A'}CC`
                  }}
                >
                  <span 
                    className="text-sm font-medium"
                    style={{ color: settings.design_header_icon_color || '#FFFFFF' }}
                  >
                    Navigation
                  </span>
                  <div style={{ color: settings.design_header_icon_color || '#FFFFFF' }}>
                    <Menu size={20} />
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-4 flex items-center gap-3">
              <input
                type="checkbox"
                id="logo-invert"
                checked={settings.design_logo_invert_dark || false}
                onChange={(e) => handleChange('design_logo_invert_dark', e.target.checked)}
                className="w-4 h-4"
              />
              <label htmlFor="logo-invert" className="text-sm">
                Logo im Dunkelmodus invertieren (für dunkle Logos auf hellem Hintergrund)
              </label>
            </div>
          </div>

          {/* Sold Banner Design */}
          <div className="bg-white p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-4">Verkauft-Banner Design</h3>
            <p className="text-sm text-[#666666] mb-6">
              Passen Sie das Aussehen des "Verkauft"-Banners an, das auf verkauften Fahrzeugen angezeigt wird.
            </p>
            
            {/* Banner Preview */}
            <div className="mb-6 p-6 bg-[#F9F9F7] rounded-lg flex items-center justify-center">
              <div className="transform -rotate-12">
                <div 
                  className="px-8 py-3 shadow-xl rounded"
                  style={{
                    background: `linear-gradient(to right, ${settings.sold_banner_color_from || '#059669'}, ${settings.sold_banner_color_via || '#22c55e'}, ${settings.sold_banner_color_to || '#059669'})`
                  }}
                >
                  <span className="text-white font-bold text-lg tracking-widest uppercase">
                    {settings.sold_banner_text || 'Verkauft'}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Color Gradient Settings */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div>
                <label className="overline block mb-2">Startfarbe</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={settings.sold_banner_color_from || '#059669'}
                    onChange={(e) => handleChange('sold_banner_color_from', e.target.value)}
                    className="w-10 h-10 rounded cursor-pointer border border-[#E5E5E5]"
                    data-testid="sold-banner-color-from"
                  />
                  <input
                    type="text"
                    value={settings.sold_banner_color_from || '#059669'}
                    onChange={(e) => handleChange('sold_banner_color_from', e.target.value)}
                    className="input-premium flex-1 font-mono text-sm"
                    placeholder="#059669"
                  />
                </div>
              </div>
              <div>
                <label className="overline block mb-2">Mittelfarbe</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={settings.sold_banner_color_via || '#22c55e'}
                    onChange={(e) => handleChange('sold_banner_color_via', e.target.value)}
                    className="w-10 h-10 rounded cursor-pointer border border-[#E5E5E5]"
                    data-testid="sold-banner-color-via"
                  />
                  <input
                    type="text"
                    value={settings.sold_banner_color_via || '#22c55e'}
                    onChange={(e) => handleChange('sold_banner_color_via', e.target.value)}
                    className="input-premium flex-1 font-mono text-sm"
                    placeholder="#22c55e"
                  />
                </div>
              </div>
              <div>
                <label className="overline block mb-2">Endfarbe</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={settings.sold_banner_color_to || '#059669'}
                    onChange={(e) => handleChange('sold_banner_color_to', e.target.value)}
                    className="w-10 h-10 rounded cursor-pointer border border-[#E5E5E5]"
                    data-testid="sold-banner-color-to"
                  />
                  <input
                    type="text"
                    value={settings.sold_banner_color_to || '#059669'}
                    onChange={(e) => handleChange('sold_banner_color_to', e.target.value)}
                    className="input-premium flex-1 font-mono text-sm"
                    placeholder="#059669"
                  />
                </div>
              </div>
            </div>

            {/* Preset Gradients */}
            <div className="mb-6">
              <label className="overline block mb-3">Vordefinierte Farbverläufe</label>
              <div className="flex flex-wrap gap-3">
                {[
                  { name: 'Smaragd', from: '#059669', via: '#22c55e', to: '#059669' },
                  { name: 'Ocean', from: '#0891b2', via: '#06b6d4', to: '#0891b2' },
                  { name: 'Violett', from: '#7c3aed', via: '#a855f7', to: '#7c3aed' },
                  { name: 'Sonnenuntergang', from: '#dc2626', via: '#f97316', to: '#dc2626' },
                  { name: 'Gold Premium', from: '#b45309', via: '#d97706', to: '#b45309' },
                  { name: 'Nacht', from: '#1e293b', via: '#475569', to: '#1e293b' },
                ].map((preset) => (
                  <button
                    key={preset.name}
                    type="button"
                    onClick={() => {
                      handleChange('sold_banner_color_from', preset.from);
                      handleChange('sold_banner_color_via', preset.via);
                      handleChange('sold_banner_color_to', preset.to);
                    }}
                    className="px-4 py-2 rounded-lg text-white text-sm font-medium transition-transform hover:scale-105 shadow-md"
                    style={{
                      background: `linear-gradient(to right, ${preset.from}, ${preset.via}, ${preset.to})`
                    }}
                  >
                    {preset.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Banner Text */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="overline block mb-2">Banner-Text (Deutsch)</label>
                <input
                  type="text"
                  value={settings.sold_banner_text || 'Verkauft'}
                  onChange={(e) => handleChange('sold_banner_text', e.target.value)}
                  className="input-premium w-full"
                  placeholder="Verkauft"
                  data-testid="sold-banner-text-de"
                />
              </div>
              <div>
                <label className="overline block mb-2">Banner-Text (Englisch)</label>
                <input
                  type="text"
                  value={settings.sold_banner_text_en || 'Sold'}
                  onChange={(e) => handleChange('sold_banner_text_en', e.target.value)}
                  className="input-premium w-full"
                  placeholder="Sold"
                  data-testid="sold-banner-text-en"
                />
              </div>
            </div>
          </div>
        </TabsContent>

        {/* API Keys */}
        <TabsContent value="api-keys" className="space-y-6">
          <div className="bg-white p-6 border border-[#E5E5E5] rounded-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Shield className="text-blue-600" size={24} />
              </div>
              <div>
                <h3 className="font-heading text-lg">API Schlüssel Konfiguration</h3>
                <p className="text-sm text-[#666666]">Sichere Verwaltung Ihrer API-Zugangsdaten</p>
              </div>
            </div>
            
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="text-sm text-amber-800 font-medium">Sicherheitshinweis</p>
                  <p className="text-sm text-amber-700 mt-1">
                    API-Schlüssel werden aus Sicherheitsgründen <strong>nicht in der Datenbank</strong> gespeichert. 
                    Sie befinden sich in der Server-Umgebungskonfiguration (<code className="bg-amber-100 px-1 rounded">.env</code> Datei).
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Resend Configuration */}
          <div className="bg-white p-6 border border-[#E5E5E5] rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">R</span>
                </div>
                <div>
                  <h3 className="font-heading text-lg">Resend</h3>
                  <p className="text-sm text-[#666666]">E-Mail-Versand Service</p>
                </div>
              </div>
              <a 
                href="https://resend.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 transition-colors"
              >
                resend.com öffnen
                <ExternalLink size={14} />
              </a>
            </div>

            {/* Editable Resend Keys with Master Password */}
            <div className="space-y-4">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <Lock className="text-amber-600 flex-shrink-0 mt-0.5" size={18} />
                  <div>
                    <p className="text-sm text-amber-800 font-medium">Master-Passwort erforderlich</p>
                    <p className="text-sm text-amber-700 mt-1">
                      Um die E-Mail-Einstellungen zu ändern, müssen Sie Ihr Admin-Passwort eingeben.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="overline block mb-2">Resend API Key</label>
                  <input
                    type="text"
                    value={settings.resend_api_key_input || ''}
                    onChange={(e) => handleChange('resend_api_key_input', e.target.value)}
                    className="input-premium w-full font-mono text-sm"
                    placeholder="re_xxxxxxxxxxxxxxxx"
                    data-testid="resend-api-key-input"
                  />
                  <p className="text-xs text-[#999999] mt-1">
                    Holen Sie sich den Key von{' '}
                    <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      resend.com/api-keys
                    </a>
                  </p>
                </div>
                <div>
                  <label className="overline block mb-2">Absender E-Mail</label>
                  <input
                    type="email"
                    value={settings.sender_email_input || ''}
                    onChange={(e) => handleChange('sender_email_input', e.target.value)}
                    className="input-premium w-full"
                    placeholder="noreply@ihre-domain.de"
                    data-testid="sender-email-input"
                  />
                  <p className="text-xs text-[#999999] mt-1">
                    Muss eine verifizierte Domain bei Resend sein
                  </p>
                </div>
              </div>
              
              {/* Webhook Info & Signing Secret */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
                <div>
                  <p className="text-sm text-blue-800">
                    <strong>📬 Webhook URL für Inbound E-Mails:</strong>
                  </p>
                  <code className="block mt-2 p-2 bg-white rounded text-xs text-blue-900 break-all">
                    {window.location.origin}/api/webhooks/inbound-email
                  </code>
                  <p className="text-xs text-blue-700 mt-2">
                    Diese URL bei Resend unter "Webhooks" eintragen, um eingehende E-Mails zu empfangen.
                  </p>
                </div>
                
                <div>
                  <label className="overline block mb-2 text-blue-800">Webhook Signing Secret</label>
                  <input
                    type="password"
                    value={settings.webhook_signing_secret || ''}
                    onChange={(e) => handleChange('webhook_signing_secret', e.target.value)}
                    className="input-premium w-full bg-white"
                    placeholder="whsec_xxxxxxxxxxxxxxxx"
                    data-testid="webhook-signing-secret"
                  />
                  <p className="text-xs text-blue-700 mt-1">
                    Das Signing Secret finden Sie bei Resend unter Webhooks → Ihr Webhook → Signing Secret
                  </p>
                </div>
              </div>

              <div>
                <label className="overline block mb-2">Master-Passwort zur Bestätigung</label>
                <input
                  type="password"
                  value={settings.master_password_confirm || ''}
                  onChange={(e) => handleChange('master_password_confirm', e.target.value)}
                  className="input-premium w-full md:w-1/2"
                  placeholder="Ihr Admin-Passwort"
                  data-testid="master-password-confirm"
                />
              </div>

              <Button
                type="button"
                onClick={async () => {
                  if (!settings.resend_api_key_input || !settings.sender_email_input || !settings.master_password_confirm) {
                    toast.error('Bitte alle Felder ausfüllen');
                    return;
                  }
                  try {
                    const { resendApi } = await import('@/lib/api');
                    await resendApi.updateKeys({
                      resend_api_key: settings.resend_api_key_input,
                      sender_email: settings.sender_email_input,
                      master_password: settings.master_password_confirm,
                      webhook_signing_secret: settings.webhook_signing_secret || null
                    });
                    toast.success('E-Mail-Einstellungen gespeichert');
                    // Clear the password field
                    handleChange('master_password_confirm', '');
                  } catch (err) {
                    // Check for 401 (wrong password)
                    if (err.response?.status === 401) {
                      toast.error('Falsches Master-Passwort! Kontaktieren Sie den Support bei Problemen.', {
                        duration: 5000
                      });
                    } else {
                      const errorMsg = err.response?.data?.detail || err.message || 'Fehler beim Speichern der E-Mail-Einstellungen';
                      toast.error(errorMsg);
                    }
                  }
                }}
                className="btn-primary flex items-center gap-2"
                data-testid="save-resend-keys"
              >
                <Shield size={16} />
                E-Mail-Einstellungen speichern
              </Button>
            </div>
          </div>

          {/* Cloudflare Turnstile CAPTCHA */}
          <div className="bg-white p-6 border border-[#E5E5E5] rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
                  <Shield className="text-white" size={20} />
                </div>
                <div>
                  <h3 className="font-heading text-lg">Cloudflare Turnstile</h3>
                  <p className="text-sm text-[#666666]">CAPTCHA-Schutz für Formulare</p>
                </div>
              </div>
              <a 
                href="https://dash.cloudflare.com/?to=/:account/turnstile" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 transition-colors"
              >
                Cloudflare Dashboard
                <ExternalLink size={14} />
              </a>
            </div>

            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-6">
              <div className="flex items-start gap-3">
                <Shield className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="text-sm text-blue-800 font-medium">Spam-Schutz</p>
                  <p className="text-sm text-blue-700 mt-1">
                    Cloudflare Turnstile schützt Ihre Formulare (Kontakt, Probefahrt, Newsletter) vor Spam und Bot-Angriffen.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {/* Site Key */}
              <div className="p-4 bg-[#F9F9F7] rounded-lg">
                <label className="overline block mb-2 text-[#666666]">TURNSTILE_SITE_KEY</label>
                <input
                  type="text"
                  value={settings.turnstile_site_key || ''}
                  onChange={(e) => handleChange('turnstile_site_key', e.target.value)}
                  className="input-premium w-full font-mono text-sm"
                  placeholder="0x4AAAAAAA..."
                  data-testid="turnstile-site-key"
                />
                <p className="text-xs text-[#999999] mt-2">
                  Der öffentliche Schlüssel für das Widget. Wird im Frontend verwendet.
                </p>
              </div>

              {/* Secret Key */}
              <div className="p-4 bg-[#F9F9F7] rounded-lg">
                <label className="overline block mb-2 text-[#666666]">TURNSTILE_SECRET_KEY</label>
                <input
                  type="password"
                  value={settings.turnstile_secret_key || ''}
                  onChange={(e) => handleChange('turnstile_secret_key', e.target.value)}
                  className="input-premium w-full font-mono text-sm"
                  placeholder="0x4AAAAAAA..."
                  data-testid="turnstile-secret-key"
                />
                <p className="text-xs text-[#999999] mt-2">
                  Der geheime Schlüssel für die Server-seitige Verifizierung. Wird im Backend verwendet.
                </p>
              </div>
            </div>

            <div className="mt-6 p-4 bg-[#F9F9F7] rounded-lg">
              <h4 className="text-sm font-medium mb-2">So erhalten Sie die Schlüssel:</h4>
              <ol className="text-xs text-[#666666] space-y-1 list-decimal list-inside">
                <li>Melden Sie sich bei <a href="https://dash.cloudflare.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">dash.cloudflare.com</a> an</li>
                <li>Navigieren Sie zu Turnstile unter dem Menü</li>
                <li>Erstellen Sie eine neue Site und kopieren Sie Site Key & Secret Key</li>
              </ol>
            </div>
          </div>

          {/* Master Password */}
          <div className="bg-white p-6 border border-[#E5E5E5] rounded-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <Lock className="text-red-600" size={20} />
              </div>
              <div>
                <h3 className="font-heading text-lg">Master-Passwort</h3>
                <p className="text-sm text-[#666666]">Notfall-Zugang für Support</p>
              </div>
            </div>

            <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="text-sm text-red-800 font-medium">Vertraulich</p>
                  <p className="text-sm text-red-700 mt-1">
                    Das Master-Passwort ermöglicht den Zugang zu <strong>allen Admin-Konten</strong>. 
                    Es ist verschlüsselt (bcrypt) in der <code className="bg-red-100 px-1 rounded">.env</code> Datei gespeichert.
                  </p>
                </div>
              </div>
            </div>

            <div className="p-4 bg-[#F9F9F7] rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <label className="overline text-[#666666]">MASTER_PASSWORD_HASH</label>
                <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">Konfiguriert</span>
              </div>
              <p className="text-sm text-[#333333] font-mono bg-white p-2 rounded border border-[#E5E5E5]">
                $2b$12$****************************
              </p>
              <p className="text-xs text-[#999999] mt-2">
                Verschlüsselter Hash - Das Klartext-Passwort ist nur dem System-Administrator bekannt.
              </p>
            </div>

            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>Verwendung:</strong> Das Master-Passwort kann anstelle des normalen Passworts 
                beim Login eingegeben werden, um Zugang zu erhalten falls das Admin-Passwort vergessen wurde.
              </p>
            </div>
          </div>
        </TabsContent>

        {/* Accounts Tab */}
        <TabsContent value="accounts" className="space-y-6">
          {/* Change Password Button */}
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-amber-100 rounded-lg">
                  <KeyRound size={24} className="text-amber-600" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-medium">Passwort ändern</h3>
                  <p className="text-sm text-[#666666]">Ändern Sie Ihr eigenes Admin-Passwort</p>
                </div>
              </div>
              <Link
                to="/admin/password"
                className="px-6 py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
              >
                <Lock size={18} />
                Passwort ändern
              </Link>
            </div>
          </div>
          
          <AdminAccounts />
        </TabsContent>
      </Tabs>
    </div>
  );
}
